import { browserAccountStateSessionRepository } from "../account/account-state-session-repository.js";
import { createLegalAcceptanceRepository } from "../account/legal-acceptance-repository.js";
import { allowsDraftLegalAcceptance, canAcceptTermsAtUrl } from "../account/legal-acceptance-contract.js";

(function () {
  const root = document.querySelector("[data-terms-legal-acceptance]");
  if (!root) return;

  function pageText(key, fallback) {
    const translated = window.__fishystuffLanguage?.t?.(key);
    return translated && translated !== key ? translated : fallback;
  }

  const checkbox = root.querySelector("[data-terms-legal-checkbox]");
  const privacyLink = root.querySelector("[data-terms-legal-privacy-link]");
  const submit = root.querySelector("[data-terms-legal-submit]");
  const status = root.querySelector("[data-terms-legal-status]");
  const version = root.querySelector("[data-terms-legal-version]");
  const locale = document.documentElement.lang === "de-DE" ? "de-DE" : "en-US";
  // Other revisions and comparisons are review-only; preview deployments select draft Terms.
  if (!canAcceptTermsAtUrl(window.location.href)) {
    root.hidden = true;
    checkbox.disabled = true;
    submit.disabled = true;
    return;
  }
  const sessionRepository = browserAccountStateSessionRepository();
  const legalRepository = createLegalAcceptanceRepository({
    resolveApiUrl: window.__fishystuffResolveApiUrl,
  });
  let selectedAccountId = null;
  let request = null;
  let submitting = false;
  let canAccept = false;

  function syncSubmit() {
    checkbox.disabled = !canAccept || submitting;
    submit.disabled = !canAccept || submitting || checkbox.checked !== true;
  }

  function versionText(descriptor) {
    const effectiveDate = new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeZone: "UTC",
    }).format(new Date(`${descriptor.effectiveDate}T00:00:00Z`));
    return `${pageText("account.legal.version", "Version")} ${descriptor.version} · ${
      allowsDraftLegalAcceptance()
        ? (locale === "de-DE" ? "Dokumentdatum" : "Document date")
        : pageText("account.legal.effective", "Effective")
    } ${effectiveDate}`;
  }

  async function loadAcceptance(accountId) {
    request?.abort();
    const controller = new AbortController();
    request = controller;
    root.hidden = true;
    canAccept = false;
    syncSubmit();
    try {
      const value = await legalRepository.status(locale, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || selectedAccountId !== accountId) return;
      canAccept = !value.accepted && canAcceptTermsAtUrl(window.location.href);
      privacyLink.href = value.current.privacy.href;
      version.textContent = versionText(value.current.terms);
      root.hidden = value.accepted;
      status.textContent = allowsDraftLegalAcceptance()
        ? (locale === "de-DE"
          ? "Testumgebung: Die Annahme dieser ausgewählten Entwurfs-Nutzungsbedingungen schaltet Testkontofunktionen frei. Dies ist keine Annahme der Produktionsbedingungen."
          : "Test environment: accepting these selected draft Terms unlocks test account features. This is not acceptance of production Terms.")
        : "";
      syncSubmit();
    } catch (_error) {
      if (controller.signal.aborted) return;
      root.hidden = false;
      canAccept = false;
      status.textContent = pageText(
        "account.legal.status_unavailable",
        "Terms acceptance status is unavailable.",
      );
      syncSubmit();
    }
  }

  checkbox.addEventListener("change", syncSubmit);
  submit.addEventListener("click", async () => {
    if (submit.disabled || !canAccept || !canAcceptTermsAtUrl(window.location.href)) return;
    submitting = true;
    syncSubmit();
    status.textContent = pageText("account.legal.recording", "Recording Terms acceptance…");
    try {
      const session = await sessionRepository.refresh();
      if (session.kind !== "ready" || session.accountId !== selectedAccountId) {
        throw new Error("account session changed");
      }
      await legalRepository.accept(locale, session);
      await sessionRepository.refresh();
      submitting = false;
      root.hidden = true;
      document.querySelector("fishy-legal-acceptance-notice")
        ?.markAccepted(selectedAccountId);
      status.textContent = "";
    } catch (_error) {
      submitting = false;
      status.textContent = pageText(
        "account.legal.failed",
        "Terms acceptance could not be recorded. Try again.",
      );
      syncSubmit();
    }
  });

  sessionRepository.subscribe((state) => {
    if (state.kind !== "ready") {
      selectedAccountId = null;
      submitting = false;
      request?.abort();
      root.hidden = true;
      return;
    }
    if (state.accountId === selectedAccountId) return;
    submitting = false;
    selectedAccountId = state.accountId;
    checkbox.checked = false;
    void loadAcceptance(state.accountId);
  });
  void sessionRepository.load();
})();
